import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { formatDateParam, useTranslate } from "helpers";
import { Routes as FinanceRoutes } from "ui/modules/finance/navigation";
import { getOverviewInvoicesAPI } from "services/api/dashboard/api";
import { OverviewInvoicesResponse } from "services/api/dashboard/types";
import { Loader } from "ui/components/Loader";
import { NoResults } from "ui/components/NoResults";
import DashboardTooltip, { TooltipDivider, TooltipRow, TooltipTitle } from "../../common/DashboardTooltip";
import CardHeader, { DeltaBadge } from "../../common/CardHeader";
import BarTooltip, { BarTooltipRow, BarTooltipTitle, LegendTooltip } from "../../common/HoverPopup";
import { compact, negative, text } from "../../common/dashboardPalette";
import { useDashboardDateRange } from "../../../context/DashboardDateRangeContext";
import { rangeLabel } from "ui/components/DateRangePicker";
import AxisLabels from "../../common/AxisLabels";

const InvoicesCard: React.FC = () => {
  const { t } = useTranslate();
  const navigate = useNavigate();
  const { range } = useDashboardDateRange();
  const [data, setData] = useState<OverviewInvoicesResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setIsLoading(true);
    getOverviewInvoicesAPI(range.from, range.to)
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setIsLoading(false));
  }, [range.from, range.to]);

  if (isLoading) return <div className="relative h-full"><Loader isLoading={true}> </Loader></div>;
  if (!data) return <div className="flex items-center justify-center h-full text-sm text-slate-400">Nema podataka</div>;

  const months = data.months.map((m) => {
    return {
      label: m.monthLabel || m.month,
      start: m.start,
      end: m.end,
      incomingCount: m.incomingCount,
      outgoingCount: m.outgoingCount,
      incomingAmount: m.incomingAmount,
      outgoingAmount: m.outgoingAmount,
    };
  });

  // Klik na stupac -> pretraga računa za taj period; rose = ulazni (tab=incoming), emerald = izlazni (tab=outgoing).
  // Bucket `end` je ekskluzivan (početak sljedećeg bucketa) pa ga za inkluzivni "Račun do" pomičemo za 1 ms unatrag.
  const handleBarClick = (tab: "incoming" | "outgoing", start: string | null, end: string | null) => {
    const params = new URLSearchParams({ tab });
    if (start) params.set("invoiceDateFrom", formatDateParam(new Date(start))!);
    if (end) params.set("invoiceDateTo", formatDateParam(new Date(new Date(end).getTime() - 1))!);
    navigate(`${FinanceRoutes.InvoiceSearchPageRouter}?${params.toString()}`);
  };

  const incoming = months.map((m) => m.incomingCount);
  const outgoing = months.map((m) => m.outgoingCount);
  const incomingAmount = months.map((m) => m.incomingAmount);
  const outgoingAmount = months.map((m) => m.outgoingAmount);
  const totalAmount = incomingAmount.map((v, i) => v + outgoingAmount[i]);
  const maxVal = Math.max(...incoming, ...outgoing, 1);

  const totalIncoming = data.totalIncomingCount;
  const totalOutgoing = data.totalOutgoingCount;
  const incomingSum = data.totalIncomingAmount;
  const outgoingSum = data.totalOutgoingAmount;
  const difference = outgoingSum - incomingSum;

  const last = months.length - 1;
  const prev = last - 1;
  const incomingChange = prev >= 0 && incoming[prev] > 0 ? ((incoming[last] - incoming[prev]) / incoming[prev]) * 100 : 0;
  const outgoingChange = prev >= 0 && outgoing[prev] > 0 ? ((outgoing[last] - outgoing[prev]) / outgoing[prev]) * 100 : 0;

  // Boje kao TotalVehiclesCard: prozirni gradient (jak na vrhu -> blijedi prema dnu) + 2px border na vrhu.
  // Tekući mjesec je nešto zasićeniji da ostane istaknut. Pune klase u stringovima — Tailwind JIT vidi sve varijante.
  const incomingBarBase = "bg-gradient-to-b from-rose-500/70 to-rose-500/30 border-t-2 border-rose-500 group-hover/bar:from-rose-500/80 group-hover/bar:to-rose-500/40";
  const incomingBarCurrent = "bg-gradient-to-b from-rose-500/90 to-rose-500/40 border-t-2 border-rose-500 group-hover/bar:from-rose-500 group-hover/bar:to-rose-500/50";
  const outgoingBarBase = "bg-gradient-to-b from-emerald-500/70 to-emerald-500/30 border-t-2 border-emerald-500 group-hover/bar:from-emerald-500/80 group-hover/bar:to-emerald-500/40";
  const outgoingBarCurrent = "bg-gradient-to-b from-emerald-500/90 to-emerald-500/40 border-t-2 border-emerald-500 group-hover/bar:from-emerald-500 group-hover/bar:to-emerald-500/50";
  const incomingDot = "bg-rose-500";
  const outgoingDot = "bg-emerald-500";

  return (
    <div className={`rounded-sm ${compact.padding} bg-white h-full flex flex-col`}>
      <CardHeader
        title="Računi"
        subtitle={
          <>
            Pregled · {rangeLabel(range)}{" "}
            <span>(ukupno {totalIncoming + totalOutgoing} računa)</span>
          </>
        }
        className="mb-10"
        right={
          <DashboardTooltip
            trigger={
              <div className="flex flex-col items-end cursor-help gap-1">
                <span className={`${compact.metric} font-bold ${difference >= 0 ? text.primary : negative.text}`}>
                  €{difference.toLocaleString("hr-HR")}
                </span>
                {data.oldestUnpaidDays > 0 && (
                  <DeltaBadge value={data.oldestUnpaidDays} inverted decimals={0} showArrow={false} />
                )}
              </div>
            }
          >
            <TooltipTitle>Pregled računa</TooltipTitle>
            <TooltipRow label="Ulazni" value={`€${incomingSum.toLocaleString("hr-HR")} (${totalIncoming})`} />
            <TooltipRow label="Izlazni" value={`€${outgoingSum.toLocaleString("hr-HR")} (${totalOutgoing})`} />
            <TooltipDivider />
            <TooltipRow label="Prosj. ulazni" value={`€${data.avgIncomingAmount.toLocaleString("hr-HR")}`} emphasis="muted" />
            <TooltipRow label="Prosj. izlazni" value={`€${data.avgOutgoingAmount.toLocaleString("hr-HR")}`} emphasis="muted" />
            <TooltipDivider />
            <TooltipRow
              label="Razlika"
              value={`€${difference.toLocaleString("hr-HR")}`}
              emphasis={difference >= 0 ? "positive" : "negative"}
            />
            {data.oldestUnpaidDays > 0 && (
              <TooltipRow label="Najstariji nenaplaćeni" value={`${data.oldestUnpaidDays} d`} emphasis="negative" />
            )}
          </DashboardTooltip>
        }
      />

      {data.totalIncomingCount === 0 && data.totalOutgoingCount === 0 ? (
        <NoResults title="Nema računa" />
      ) : (
        <>
      <div className="mx-auto mb-2 w-full flex-1 flex flex-col">
        <div className="flex justify-between gap-2 mb-2 flex-1 min-h-[120px]">
          {months.map((m, i) => {
            const incomingHeight = (incoming[i] / maxVal) * 100;
            const outgoingHeight = (outgoing[i] / maxVal) * 100;
            const isCurrent = i === last;
            return (
              <div
                key={`${m.label}-${i}`}
                className="flex flex-col items-center flex-1 h-full group/bar relative"
              >
                <div className="w-full flex-1 flex items-end justify-center gap-0.5">
                  <div
                    className={`flex-1 rounded-sm origin-bottom animate-growUp transition-all cursor-pointer ${isCurrent ? incomingBarCurrent : incomingBarBase}`}
                    style={{ height: `${Math.max(incomingHeight, 5)}%` }}
                    onClick={() => handleBarClick("incoming", m.start, m.end)}
                  />
                  <div
                    className={`flex-1 rounded-sm origin-bottom animate-growUp transition-all cursor-pointer ${isCurrent ? outgoingBarCurrent : outgoingBarBase}`}
                    style={{ height: `${Math.max(outgoingHeight, 5)}%` }}
                    onClick={() => handleBarClick("outgoing", m.start, m.end)}
                  />
                </div>
                <BarTooltip>
                  <BarTooltipTitle>{m.label}</BarTooltipTitle>
                  <BarTooltipRow label={`Ulazni (${incoming[i]})`} value={`€${incomingAmount[i].toLocaleString("hr-HR")}`} emphasis="negative" />
                  <BarTooltipRow label={`Izlazni (${outgoing[i]})`} value={`€${outgoingAmount[i].toLocaleString("hr-HR")}`} emphasis="positive" />
                </BarTooltip>
              </div>
            );
          })}
        </div>
        <div className="flex justify-between gap-2">
          <AxisLabels labels={months.map((m) => m.label)} className={`text-sm ${text.subtle}`} />
        </div>
      </div>

      <div className="relative group flex flex-wrap items-center justify-center gap-x-4 gap-y-2 mt-3 cursor-help">
        <div className="flex items-center gap-1.5">
          <span className={`w-2.5 h-2.5 rounded-sm ${incomingDot}`} />
          <span className={`text-sm ${text.muted}`}>Ulazni <span className="font-medium text-slate-700">{totalIncoming}</span></span>
          <DeltaBadge value={incomingChange} inverted />
        </div>
        <div className="flex items-center gap-1.5">
          <span className={`w-2.5 h-2.5 rounded-sm ${outgoingDot}`} />
          <span className={`text-sm ${text.muted}`}>Izlazni <span className="font-medium text-slate-700">{totalOutgoing}</span></span>
          <DeltaBadge value={outgoingChange} />
        </div>
        <LegendTooltip>
          <BarTooltipTitle>Promjena vs prethodni mjesec</BarTooltipTitle>
          <BarTooltipRow label="Ulazni" value={`${prev >= 0 ? incoming[prev] : 0} → ${incoming[last] ?? 0}`} emphasis="negative" />
          <BarTooltipRow label="Izlazni" value={`${prev >= 0 ? outgoing[prev] : 0} → ${outgoing[last] ?? 0}`} emphasis="positive" />
        </LegendTooltip>
      </div>
        </>
      )}
    </div>
  );
};

export default InvoicesCard;
